import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Star, Sparkles, Save, Send, Mail, X, Paperclip, Loader2 } from 'lucide-react';
import api from '../api';
import FileUpload, { FileChip } from './FileUpload';

const OBJECTIVES = [
  'Understood concept', 'Applied independently', 'Completed activity',
  'Completed project', 'Demonstrated creativity', 'Needs revision',
];
const PARTICIPATION = ['Excellent', 'Very Good', 'Good', 'Average', 'Needs Encouragement'];
const BEHAVIOUR = ['Excellent', 'Good', 'Fair', 'Distracted', 'Needs Attention'];
const MOODS = [
  { key: 'excellent', emoji: '😀', label: 'Excellent' },
  { key: 'happy', emoji: '🙂', label: 'Happy' },
  { key: 'neutral', emoji: '😐', label: 'Neutral' },
  { key: 'confused', emoji: '😕', label: 'Confused' },
  { key: 'low', emoji: '😞', label: 'Low Motivation' },
];
const ASSIGNMENT = ['No Assignment', 'Assigned', 'Submitted', 'Pending Review'];
const COMPLETION = [
  { key: 'completed_successfully', label: 'Completed Successfully' },
  { key: 'partially_completed', label: 'Partially Completed' },
  { key: 'student_absent', label: 'Student Absent' },
  { key: 'teacher_absent', label: 'Teacher Absent' },
  { key: 'rescheduled', label: 'Rescheduled' },
  { key: 'technical_issue', label: 'Technical Issue' },
];

const EMPTY = {
  topics_covered: '',
  objectives: [],
  participation: '',
  understanding_stars: 0,
  behaviour: '',
  homework_text: '',
  homework_attachments: [],
  parent_update: '',
  teacher_notes: '',
  next_class_plan: '',
  completion_status: 'completed_successfully',
  student_mood: '',
  assignment_status: 'No Assignment',
  teacher_effort_stars: 0,
  ai_used: false,
};

function StarRow({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n === value ? 0 : n)}
          className="p-1 hover:scale-110 transition-transform"
          data-testid={`${testid}-${n}`}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <Star className={`w-6 h-6 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
        </button>
      ))}
      <span className="ml-2 text-xs font-bold text-slate-500">{value || 0}/5</span>
    </div>
  );
}

/**
 * Class Feedback dialog. Loads (or scaffolds) feedback for a given class_id,
 * autosaves as draft every ~5s, and offers Submit + Save & Send to Parent.
 */
export default function ClassFeedbackForm({ classId, open, onClose, onSubmitted }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);   // { student_name, teacher_name, class_date, class_time, duration_minutes, course, class_number }
  const [form, setForm] = useState(EMPTY);
  const [aiLoading, setAiLoading] = useState(false);
  const dirtyRef = useRef(false);
  const submittingRef = useRef(false);

  const upd = useCallback((patch) => {
    dirtyRef.current = true;
    setForm(prev => ({ ...prev, ...patch }));
  }, []);

  const load = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    try {
      const res = await api.get(`/feedback/class/${classId}`);
      const d = res.data || {};
      setMeta({
        student_name: d.student_name,
        teacher_name: d.teacher_name,
        class_date: d.class_date,
        class_time: d.class_time,
        duration_minutes: d.duration_minutes,
        course: d.course || d.vertical,
        vertical: d.vertical,
        topic_name: d.topic_name,
        class_number: d.class_number,
        status: d.status,
      });
      setForm({
        topics_covered: d.topics_covered || '',
        objectives: d.objectives || [],
        participation: d.participation || '',
        understanding_stars: d.understanding_stars || 0,
        behaviour: d.behaviour || '',
        homework_text: d.homework_text || '',
        homework_attachments: d.homework_attachments || [],
        parent_update: d.parent_update || '',
        teacher_notes: d.teacher_notes || '',
        next_class_plan: d.next_class_plan || '',
        completion_status: d.completion_status || 'completed_successfully',
        student_mood: d.student_mood || '',
        assignment_status: d.assignment_status || 'No Assignment',
        teacher_effort_stars: d.teacher_effort_stars || 0,
        ai_used: d.ai_used || false,
      });
      dirtyRef.current = false;
    } catch (e) {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (open && classId) load();
  }, [open, classId, load]);

  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);

  // Auto-save draft every 5s while dirty (interval never resets on keystroke)
  useEffect(() => {
    if (!open || !classId) return;
    const t = setInterval(async () => {
      if (!dirtyRef.current || submittingRef.current) return;
      setSaving(true);
      try {
        await api.post('/feedback/draft', { class_id: classId, ...formRef.current });
        dirtyRef.current = false;
      } catch { /* silent */ }
      finally { setSaving(false); }
    }, 5000);
    return () => clearInterval(t);
  }, [open, classId]);

  const toggleObjective = (obj) => {
    upd({ objectives: form.objectives.includes(obj) ? form.objectives.filter(o => o !== obj) : [...form.objectives, obj] });
  };

  const handleUploaded = (fileMeta) => {
    upd({
      homework_attachments: [
        ...form.homework_attachments,
        { file_id: fileMeta.id, url: fileMeta.url, filename: fileMeta.filename || fileMeta.original_filename },
      ],
    });
  };
  const removeAttachment = (fid) => {
    upd({ homework_attachments: form.homework_attachments.filter(a => a.file_id !== fid) });
  };

  const doAiDraft = async () => {
    setAiLoading(true);
    try {
      const res = await api.post('/feedback/ai-draft', {
        class_id: classId,
        topics_covered: form.topics_covered,
        participation: form.participation,
        understanding_stars: form.understanding_stars,
        homework_text: form.homework_text,
      });
      if (res.data?.parent_update) {
        upd({ parent_update: res.data.parent_update, ai_used: true });
        toast.success('AI draft ready — review and edit before submitting');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI draft failed');
    } finally { setAiLoading(false); }
  };

  const handleSubmit = async (mode) => {
    if (!form.completion_status) {
      toast.error('Please choose a class completion status');
      return;
    }
    if (mode === 'sent_to_parent' && !(form.parent_update || '').trim()) {
      toast.error('Add a Parent Update before sending to parent');
      return;
    }
    submittingRef.current = true;
    setSaving(true);
    try {
      const url = mode === 'sent_to_parent' ? '/feedback/send-to-parent' : '/feedback/submit';
      await api.post(url, { class_id: classId, ...form });
      toast.success(mode === 'sent_to_parent' ? 'Feedback sent to parent' : 'Feedback submitted');
      if (onSubmitted) onSubmitted();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  const handleDraft = async () => {
    setSaving(true);
    try {
      await api.post('/feedback/draft', { class_id: classId, ...form });
      dirtyRef.current = false;
      toast.success('Draft saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const parentCount = (form.parent_update || '').length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="class-feedback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Class Feedback</span>
            {saving && <span className="text-xs text-slate-500 font-medium inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> auto-saving…</span>}
          </DialogTitle>
          <DialogDescription>
            {meta && (
              <span className="text-xs text-slate-600">
                {meta.student_name} · {meta.class_date} · {meta.class_time} · {meta.duration_minutes} min · Class #{meta.class_number || '—'} · {meta.course || 'Class'}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…</div>
        ) : (
          <div className="space-y-5">
            {/* 1. Topics Covered */}
            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <Label className="text-sm font-bold text-slate-900">1. Topics Covered</Label>
              <Textarea rows={3} value={form.topics_covered} onChange={e => upd({ topics_covered: e.target.value })}
                placeholder={"• Variables\n• Loops\n• Scratch Animations"} className="mt-2" data-testid="fb-topics" />
            </section>

            {/* 2. Learning Objectives */}
            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <Label className="text-sm font-bold text-slate-900">2. Learning Objectives Achieved</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {OBJECTIVES.map(o => (
                  <label key={o} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.objectives.includes(o)}
                      onChange={() => toggleObjective(o)}
                      className="w-4 h-4 accent-indigo-600"
                      data-testid={`fb-obj-${o.replace(/\s+/g, '-').toLowerCase()}`}
                    />
                    <span>{o}</span>
                  </label>
                ))}
              </div>
            </section>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* 3. Participation */}
              <section className="bg-white rounded-xl border border-slate-200 p-4">
                <Label className="text-sm font-bold text-slate-900">3. Student Participation</Label>
                <Select value={form.participation} onValueChange={v => upd({ participation: v })}>
                  <SelectTrigger className="mt-2" data-testid="fb-participation"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{PARTICIPATION.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </section>

              {/* 4. Understanding */}
              <section className="bg-white rounded-xl border border-slate-200 p-4">
                <Label className="text-sm font-bold text-slate-900">4. Student Understanding</Label>
                <div className="mt-2">
                  <StarRow value={form.understanding_stars} onChange={v => upd({ understanding_stars: v })} testid="fb-understanding" />
                </div>
              </section>

              {/* 5. Behaviour */}
              <section className="bg-white rounded-xl border border-slate-200 p-4">
                <Label className="text-sm font-bold text-slate-900">5. Behaviour & Engagement</Label>
                <Select value={form.behaviour} onValueChange={v => upd({ behaviour: v })}>
                  <SelectTrigger className="mt-2" data-testid="fb-behaviour"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{BEHAVIOUR.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </section>

              {/* 12. Assignment Status */}
              <section className="bg-white rounded-xl border border-slate-200 p-4">
                <Label className="text-sm font-bold text-slate-900">12. Assignment Status</Label>
                <Select value={form.assignment_status} onValueChange={v => upd({ assignment_status: v })}>
                  <SelectTrigger className="mt-2" data-testid="fb-assignment"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{ASSIGNMENT.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </section>
            </div>

            {/* 6. Homework Assigned */}
            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <Label className="text-sm font-bold text-slate-900">6. Homework Assigned</Label>
              <Textarea rows={3} value={form.homework_text} onChange={e => upd({ homework_text: e.target.value })}
                placeholder="e.g. Complete Level 3 Game, Practice Multiplication, Revise Fractions" className="mt-2" data-testid="fb-homework" />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <FileUpload
                  context="homework"
                  refId={classId}
                  multiple
                  variant="compact"
                  label="Attach file"
                  dataTestid="fb-homework-upload"
                  onUploaded={handleUploaded}
                />
                {form.homework_attachments.map(a => (
                  <div key={a.file_id} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs" data-testid={`fb-attach-${a.file_id}`}>
                    <Paperclip className="w-3 h-3 text-slate-500" />
                    <span className="truncate max-w-[160px] font-semibold text-slate-700">{a.filename}</span>
                    <button type="button" onClick={() => removeAttachment(a.file_id)} className="text-slate-400 hover:text-red-500" data-testid={`fb-attach-del-${a.file_id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </section>

            {/* 7. Parent Update */}
            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-bold text-slate-900">7. Parent Update</Label>
                <button type="button" onClick={doAiDraft} disabled={aiLoading}
                  className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                  data-testid="fb-ai-draft-btn"
                >
                  {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  Generate with AI
                </button>
              </div>
              <Textarea rows={3} value={form.parent_update} onChange={e => upd({ parent_update: e.target.value.slice(0, 300) })}
                placeholder="Short, parent-friendly summary (max 300 chars)" className="mt-2" data-testid="fb-parent-update" />
              <p className={`text-[11px] mt-1 text-right ${parentCount > 280 ? 'text-red-600 font-bold' : 'text-slate-400'}`}>{parentCount}/300</p>
            </section>

            {/* 8. Teacher Notes */}
            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <Label className="text-sm font-bold text-slate-900">8. Teacher Notes <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-2">Internal — Teachers & Admins only</span></Label>
              <Textarea rows={3} value={form.teacher_notes} onChange={e => upd({ teacher_notes: e.target.value })}
                placeholder="Not shared with parents" className="mt-2" data-testid="fb-teacher-notes" />
            </section>

            {/* 9. Next Class Plan */}
            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <Label className="text-sm font-bold text-slate-900">9. Next Class Plan</Label>
              <Textarea rows={2} value={form.next_class_plan} onChange={e => upd({ next_class_plan: e.target.value })}
                placeholder="e.g. Introduce Variables, Continue Project, Quiz, Debugging" className="mt-2" data-testid="fb-next-plan" />
            </section>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* 10. Class Completion Status */}
              <section className="bg-white rounded-xl border border-slate-200 p-4">
                <Label className="text-sm font-bold text-slate-900">10. Class Completion Status</Label>
                <div className="mt-2 space-y-1.5">
                  {COMPLETION.map(c => (
                    <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name="completion" value={c.key} checked={form.completion_status === c.key}
                        onChange={() => upd({ completion_status: c.key })}
                        className="accent-indigo-600" data-testid={`fb-completion-${c.key}`} />
                      {c.label}
                    </label>
                  ))}
                </div>
              </section>

              {/* 11. Student Mood */}
              <section className="bg-white rounded-xl border border-slate-200 p-4">
                <Label className="text-sm font-bold text-slate-900">11. Student Mood</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {MOODS.map(m => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => upd({ student_mood: form.student_mood === m.key ? '' : m.key })}
                      className={`px-3 py-2 rounded-lg border text-sm font-semibold transition ${form.student_mood === m.key ? 'border-indigo-600 bg-indigo-50 text-indigo-800' : 'border-slate-200 hover:border-slate-300'}`}
                      data-testid={`fb-mood-${m.key}`}
                      title={m.label}
                    >
                      <span className="text-lg mr-1">{m.emoji}</span>{m.label}
                    </button>
                  ))}
                </div>

                <div className="mt-4">
                  <Label className="text-sm font-bold text-slate-900">13. Teacher Effort <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-2">Optional · Internal</span></Label>
                  <StarRow value={form.teacher_effort_stars} onChange={v => upd({ teacher_effort_stars: v })} testid="fb-effort" />
                </div>
              </section>
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white/95 backdrop-blur pt-4 border-t border-slate-200 flex flex-wrap items-center justify-end gap-2">
              <Button variant="outline" onClick={onClose} data-testid="fb-cancel">Cancel</Button>
              <Button variant="secondary" onClick={handleDraft} disabled={saving} data-testid="fb-save-draft">
                <Save className="w-4 h-4 mr-1" /> Save Draft
              </Button>
              <Button onClick={() => handleSubmit('submitted')} disabled={saving} data-testid="fb-submit">
                <Send className="w-4 h-4 mr-1" /> Submit Feedback
              </Button>
              <Button onClick={() => handleSubmit('sent_to_parent')} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fb-send-to-parent">
                <Mail className="w-4 h-4 mr-1" /> Save & Send to Parent
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
